import {Injectable} from '@angular/core';
import {Observable} from "rxjs";
import {ApiService} from "./api.service";
import {CommonUtils} from "../utils/common";
import {VetineoApiResponse} from "../models/vetineo-api-response.model";
import {map, take} from "rxjs/operators";
import {DataGridServiceInterface} from "./data-grid-service.interface";
import {Snapshot} from "../models/snpashot.model";
import {PartnerBannerActionsDgSearchResults} from "../models/datagrid-search-results/partner-banner-actions.dg-search-results.model";
import {PartnerBannerAction} from "../models/partner-banner-action.model";

/**
 * Serwis warstwy danych odpowiedzialny za analitykę i telemetrię kampanii reklamowych.
 * Służy do pobierania i analizy logów zdarzeń (Actions) związanych z banerami partnerów,
 * takich jak kliknięcia, wyświetlenia czy konwersje. Implementuje interfejs tabeli DataGrid.
 */
@Injectable({
  providedIn: 'root'
})
export class PartnerBannerActionsService implements DataGridServiceInterface<Observable<PartnerBannerActionsDgSearchResults>> {

  constructor(
    private apiService: ApiService,
    public commonUtils : CommonUtils,
  ) {
  }

  /**
   * Pobiera paginowaną listę zarejestrowanych akcji/zdarzeń dla kampanii banerowych.
   *
   * @param limit Maksymalna liczba rekordów (zdarzeń) na stronę.
   * @param offset Przesunięcie wyników względem początku zbioru.
   * @param sortOrder Kierunek sortowania (np. od najnowszych: 'DESC').
   * @param orderBy Nazwa kolumny, według której aplikowane jest sortowanie (np. data zdarzenia).
   * @param showDeletedObjects Flaga decydująca o uwzględnieniu zdarzeń zarchiwizowanych.
   * @returns Observable emitujący ustandaryzowany obiekt wyników analitycznych dla tabeli.
   */
  getResults(limit: number, offset: number, sortOrder: string, orderBy: string, showDeletedObjects: boolean = false): Observable<PartnerBannerActionsDgSearchResults> {
    return this.apiService.get<VetineoApiResponse>(`/dashboard/partner-banner-actions/${showDeletedObjects}/${limit}/${offset}/${sortOrder}/${orderBy}`, {}).pipe(take(1), map(vetineoApiResponse => {
      if (vetineoApiResponse.ok) {
        return new PartnerBannerActionsDgSearchResults(vetineoApiResponse.response.total, this.commonUtils.bestTypedArrayCopyEver(new PartnerBannerAction(), [], vetineoApiResponse.response.results), vetineoApiResponse.response.csrfToken);
      } else {
        return undefined;
      }
    }));
  }

  /**
   * Wyszukuje i filtruje zdarzenia analityczne w oparciu o zaawansowane parametry
   * (np. wyciągnięcie kliknięć tylko dla konkretnego banera, partnera lub w określonym przedziale czasu).
   *
   * @param limit Rozmiar strony.
   * @param offset Przesunięcie.
   * @param searchFieldsData Mapa zawierająca zdefiniowane filtry analityczne.
   * @param sortOrder Kierunek sortowania.
   * @param orderBy Nazwa kolumny sortującej.
   * @param showDeletedObjects Flaga widoczności usuniętych rekordów.
   * @returns Observable emitujący przefiltrowaną listę zdarzeń dla DataGrid.
   */
  searchForResults(limit: number, offset: number, searchFieldsData: Map<string, string>, sortOrder: string, orderBy: string, showDeletedObjects: boolean = false): Observable<PartnerBannerActionsDgSearchResults> {
    const data = {
      limit: limit,
      offset: offset,
      searchFieldsData: this.commonUtils.mapToSearchFieldList(searchFieldsData),
      sortOrder: sortOrder,
      orderBy: orderBy,
      showDeletedObjects: showDeletedObjects
    }
    return this.apiService.post<VetineoApiResponse>(`/dashboard/partner-banner-actions/search`, data).pipe(map(vetineoApiResponse => {
      if (vetineoApiResponse.ok) {
        return new PartnerBannerActionsDgSearchResults(vetineoApiResponse.response.total, this.commonUtils.bestTypedArrayCopyEver(new PartnerBannerAction(), [], vetineoApiResponse.response.results), vetineoApiResponse.response.csrfToken);
      } else {
        return undefined;
      }
    }));
  }

  /**
   * Pobiera historię zmian audytowych (Snapshot) dla konkretnego rekordu akcji.
   *
   * @param uuid Unikalny identyfikator konkretnej akcji.
   * @returns Observable emitujący tablicę migawek (Snapshot) z historią.
   */
  showChanges(uuid: string): Observable<Snapshot[]> {
    return this.apiService.get<VetineoApiResponse>(`/dashboard/partner-banner-actions/changes/${uuid}`).pipe(take(1), map(vetineoApiResponse => {
      if (vetineoApiResponse.ok) {
        return this.commonUtils.bestTypedArrayCopyEver(new Snapshot(), [], vetineoApiResponse.response);
      } else {
        return undefined;
      }
    }));
  }

}
